/** Map backend booking/inspection error codes to user-friendly copy. */

/** Mirrors `geo_utils.DEFAULT_ONE_WAY_MAX_KM` (env `ONE_WAY_MAX_KM`, default 200). */
export const ONE_WAY_MAX_KM = 200;

export function formatBookingError(detail) {
  if (!detail) return "Something went wrong — please try again.";
  if (typeof detail !== "string") return String(detail);
  if (detail === "kyc_required") return "Verify your identity before booking.";
  if (detail === "profile_photo_required") {
    return "Upload a real profile photo in settings before listing or renting a vehicle.";
  }
  if (detail === "kyc_expired") return "Your verified ID has expired. Please complete identity verification again.";
  if (detail === "kyc_dl_required" || detail === "kyc_canada_dl_only") {
    return "A valid driver's licence is required to rent on Z-Cars.";
  }
  if (detail === "kyc_id_back_required") return "Please capture the back of your driver's licence.";
  if (detail === "renter_underage") return "You must be at least 24 years old to rent a vehicle on Z-Cars.";
  if (detail === "date_of_birth_required") return "Add your date of birth in profile settings before booking.";
  if (detail === "dates_in_scheduled_pause") return "Those dates fall in a scheduled listing pause. Choose different dates.";
  if (detail === "dates_blocked_by_host") return "Some dates are blocked by the host. Choose different dates.";
  if (detail === "dates_not_rentable") return "Your trip includes days when this host has not marked the vehicle as rentable.";
  if (detail === "no_rentable_days_defined") return "This host has not opened any rentable days yet. Try another vehicle or dates.";
  if (detail === "ambiguous_local_time") return "That clock time occurs twice because daylight saving time is ending. Choose a different time.";
  if (detail === "nonexistent_local_time") return "That clock time does not exist because daylight saving time is starting. Choose a different time.";
  if (detail === "booking_timezone_unresolved") return "We could not determine the vehicle’s local timezone. Ask the host to verify the listing location.";
  if (detail.startsWith("booking_timezone_unavailable")) return "The vehicle timezone service is temporarily unavailable. Try again shortly.";
  if (detail === "active_booking_exists") {
    return "You already have an open trip. Finish or cancel it before booking another vehicle.";
  }
  if (detail === "location_address_required") return "Enter the full street address where the car is kept.";
  if (detail === "return_date_cannot_be_shortened") return "The current return cannot be shortened.";
  if (detail === "return_date_extension_not_allowed") return "This host does not allow return-date extensions.";
  if (detail === "return_address_change_not_allowed") return "This host does not allow return-address changes.";
  if (detail === "amendment_no_changes") return "Choose a later return or a different return address.";
  if (detail === "one_way_not_allowed") return "This listing does not allow returns to a different address.";
  if (detail === "baseline_rescan_required") {
    return "Complete a fresh live-camera AI condition scan that matches your updated listing before resubmitting.";
  }
  if (detail === "exceeds_max_parking_hours") return "This booking exceeds the host's maximum stay limit.";
  if (detail === "outside_operating_hours") return "Check-in or check-out falls outside the host's operating hours.";
  if (detail.startsWith("pickup_lead_time:")) {
    const hours = detail.split(":")[1] || "1";
    return `Pick-up must be at least ${hours} hour(s) from now for this vehicle.`;
  }
  if (detail === "insufficient_wallet_balance") {
    return "Your Z-Wallet balance changed — refresh the quote and try again.";
  }
  if (detail === "amount_below_stripe_minimum" || detail === "stripe_minimum_amount") {
    return "Amount due is below the card minimum — apply more Z-Wallet credit or adjust the trip.";
  }
  if (detail === "prepaid_use_booking_create") {
    return "This trip is fully covered — confirm without card checkout.";
  }
  if (detail === "payment_required") {
    return "Card payment is required for this booking.";
  }
  if (detail === "pending_host_escrow" || detail === "open_host_bookings" || detail === "wallet_credit_ineligible") {
    return "Z-Wallet credit isn’t available while you have pending host rents or open host trips.";
  }
  if (detail.startsWith("one_way_distance_exceeded:")) {
    const km = detail.split(":")[1] || "";
    if (km) {
      return `Drop-off is about ${km} km from pick-up. One-way returns must be within ${ONE_WAY_MAX_KM} km.`;
    }
    return `Drop-off is too far from pick-up. One-way returns must be within ${ONE_WAY_MAX_KM} km.`;
  }
  if (detail.startsWith("one_way_address_unresolved:")) {
    return "We couldn't verify that drop-off address. Check the full street address and postal code.";
  }
  if (
    detail === "one_way_geocoding_unavailable:provider_not_configured"
    || detail === "one_way_geocoding_unavailable:google_api_key_missing"
  ) {
    return "Return-address verification is not configured on the server. Please contact support, or try again after Maps geocoding is enabled.";
  }
  if (detail.startsWith("one_way_geocoding_unavailable:google_request_denied")) {
    return "Return-address verification was denied by the maps provider. Please contact support.";
  }
  if (detail.startsWith("one_way_geocoding_unavailable:")) {
    return "Address verification is temporarily unavailable. Please try again before booking.";
  }
  if (detail === "Vehicle is not available") return "This vehicle is not available right now.";
  if (detail === "contact_info_blocked") {
    return "For safety, phone numbers, emails, and messaging-app contacts can’t be shared here. Keep coordinating in this chat.";
  }
  return detail;
}

export function formatInspectionSessionError(detail) {
  if (!detail) return "Inspection session error — try refreshing the QR link.";
  if (typeof detail !== "string") return String(detail);
  if (detail === "session_not_found") return "This scan link expired or is invalid.";
  if (detail.startsWith("session_not_active")) {
    return "This scan session expired. Tap Refresh QR to get a new link and continue.";
  }
  if (detail === "duplicate_photo_in_inspection") return "Each angle must be a distinct photo — reuse detected.";
  if (detail.startsWith("missing_angles:")) {
    const missing = detail.replace("missing_angles:", "").replace(/,/g, ", ");
    return `Still need photos: ${missing}`;
  }
  return detail;
}

export function formatInspectionSubmitError(detail) {
  if (!detail) return "Inspection failed — please try again.";
  if (typeof detail !== "string") return String(detail);
  if (detail === "odometer_required") return "Enter the odometer reading in km.";
  if (detail === "fuel_or_battery_required") return "Enter fuel or battery charge % before pickup.";
  if (detail === "odometer_decreased") return "Return odometer cannot be lower than pickup.";
  if (detail === "complete_pickup_inspection_first") return "Complete pickup inspection first.";
  if (detail === "gallery_upload_not_allowed") {
    return "Condition scans require live camera photos — gallery uploads are not accepted.";
  }
  if (detail === "duplicate_photo_in_inspection") {
    return "Each angle must be a distinct photo — reuse detected.";
  }
  if (detail === "duplicate_photo_reused") {
    return "One or more photos match a prior trip — live capture required.";
  }
  if (detail === "file_owner_mismatch" || detail === "file_booking_mismatch") {
    return "Photo verification failed — retake all angles with the camera.";
  }
  if (detail === "vehicle_identity_mismatch") {
    return "Photos do not match the listed vehicle — contact support if this is an error.";
  }
  if (detail === "plate_mismatch") {
    return "The license plate in your photos does not match the vehicle from pick-up or the listing. Retake clear front/rear plate photos.";
  }
  return detail;
}
